import type { Calibration, Point } from './types'
import { clamp, distance } from './utils'

export function createCalibration(p1: Point, p2: Point, distanceMm: number): Omit<Calibration, 'id' | 'archiveId' | 'createdAt'> {
  if (!Number.isFinite(distanceMm) || distanceMm <= 0) throw new Error('La distanza del riferimento deve essere maggiore di zero.')
  const pixels = distance(p1, p2)
  if (!Number.isFinite(pixels) || pixels <= 0) throw new Error('Marca due tacche diverse del riferimento.')
  return { observationId: '', p1, p2, distanceMm, pixelsPerMm: pixels / distanceMm, method: 'physical-reference-two-marks' }
}

export function diameterFromPoints(p1: Point, p2: Point, calibration: Pick<Calibration, 'pixelsPerMm'>): number {
  if (!Number.isFinite(calibration.pixelsPerMm) || calibration.pixelsPerMm <= 0) throw new Error('Calibrazione non valida.')
  return distance(p1, p2) / calibration.pixelsPerMm
}

export function normalizePoint(point: Point, width: number, height: number): Point {
  return { x: clamp(point.x / Math.max(1, width), 0, 1), y: clamp(point.y / Math.max(1, height), 0, 1) }
}

export function denormalizePoint(point: Point, width: number, height: number): Point {
  return { x: clamp(point.x, 0, 1) * width, y: clamp(point.y, 0, 1) * height }
}

export function rotatePoint(point: Point, width: number, height: number, quarterTurns: number): { point: Point; width: number; height: number } {
  const turns = ((quarterTurns % 4) + 4) % 4
  if (turns === 0) return { point, width, height }
  if (turns === 1) return { point: { x: height - point.y, y: point.x }, width: height, height: width }
  if (turns === 2) return { point: { x: width - point.x, y: height - point.y }, width, height }
  return { point: { x: point.y, y: width - point.x }, width: height, height: width }
}

export function gridStepMm(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) return 1
  if (zoom >= 4) return 0.5
  if (zoom >= 2) return 1
  if (zoom >= 1) return 2
  if (zoom >= 0.5) return 5
  return 10
}

export function gridLines(widthPx: number, heightPx: number, pixelsPerMm: number, zoom: number): { vertical: number[]; horizontal: number[]; stepMm: number } {
  if (!Number.isFinite(pixelsPerMm) || pixelsPerMm <= 0) return { vertical: [], horizontal: [], stepMm: gridStepMm(zoom) }
  const stepMm = gridStepMm(zoom)
  const stepPx = pixelsPerMm * stepMm * zoom
  if (stepPx < 3) return { vertical: [], horizontal: [], stepMm }
  const vertical: number[] = []
  const horizontal: number[] = []
  for (let x = 0; x <= widthPx; x += stepPx) vertical.push(Number(x.toFixed(2)))
  for (let y = 0; y <= heightPx; y += stepPx) horizontal.push(Number(y.toFixed(2)))
  return { vertical, horizontal, stepMm }
}
